import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Fade,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  Zoom
} from '@mui/material';
import { useState } from 'react';
import {
  CONST_BUSINESS_ACCOUNT_EXTERNAL_ID,
  CONST_BUSINESS_ACCOUNT_ID,
  CONST_BUSINESS_HOST,
  CONST_ONBOARD_AWS_POLICY_LINK,
  CONST_ONBOARD_CLOUDFORMATION_LINK,
  CONST_SITE_LABEL,
  ONBOARDING_STEPS_CASE,
  STORAGE_KEYS,
  checkAndNavigateToPrice,
  navigateToDashboardPostLogin,
  validateAccountARN,
  validateAccountId,
} from './common/Helper';
import './common/Styles.css'
import './common/OnboardingStyles.css'
import styled from '@emotion/styled';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../state/AuthContext';
import { errorColor } from './common/ColorCodes';
import RegistrationApiService from '../services/RegistrationApiService';
import DebugService from '../services/DebugService';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

// Modern Icons
import {
  CloudUpload as CloudUploadIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  AutoAwesome as AutoAwesomeIcon,
  Build as ManualModeIcon,
  SmartToy as AutomationIcon,
  AccountTree as AccountTreeIcon,
  VpnKey as VpnKeyIcon,
  Policy as PolicyIcon,
  Link as LinkIcon,
  Rocket as RocketIcon
} from '@mui/icons-material';

export const NoteDetails = () => {
  return (
    <>
      <Typography><span style={{ fontWeight: 600 }}>Note:</span> We are only requesting <span style={{ fontWeight: 600 }}>read-only access</span> along with permissions to <span style={{ fontWeight: 600 }}>start/stop instances</span> and manage <span style={{ fontWeight: 600 }}>auto-scaling (scale up/down)</span>. We <span style={{ fontWeight: 600 }}>strictly do not access or read any content</span> stored in your AWS environment.</Typography>
      <Typography>You are welcome to review the full permission set here:</Typography>
      <Typography><a
        href={CONST_ONBOARD_AWS_POLICY_LINK}
        rel="noreferrer"
        target='_blank'
        className='action-link-core'
      >{CONST_ONBOARD_AWS_POLICY_LINK}
      </a></Typography>

      <ol>
        <li><span style={{ fontWeight: 600 }}>Log in</span> to your AWS Management Console.</li>
        <li>Go to <span style={{ fontWeight: 600 }}>Compute Optimizer:</span></li>
        <li>You can search for <span style={{ fontWeight: 600 }}>"Compute Optimizer"</span> in the AWS Console search bar.</li>
        <li>Or visit:&nbsp;
          <a
            href='https://console.aws.amazon.com/compute-optimizer/'
            rel="noreferrer"
            target='_blank'
            className='action-link-core'
          >https://console.aws.amazon.com/compute-optimizer/
          </a>
        </li>
        <li>On the <span style={{ fontWeight: 600 }}>Compute Optimizer dashboard</span>, click. <span style={{
          backgroundColor: '#FF9A00',
          padding: '4px 11px',
          borderRadius: '12px',
          fontSize: '14px'
        }}>Get Started</span> and <span style={{
          backgroundColor: '#FF9A00',
          padding: '4px 11px',
          borderRadius: '12px',
          fontSize: '14px'
        }}>Opt in</span> and (if not already enrolled)</li>
      </ol>
    </>
  )
}

export const Step1Details = () => {
  return (
    <>
      <Typography className='step-details-heading'>Step 1: Create a role and establish trust</Typography>
      <Typography className='step-details-text'>
        Use the AWS IAM console to create a new role. This will allow us to securely retrieve
        monitoring data from your AWS account. To get started, open the
        <span style={{
          fontStyle: 'italic',
          color: '#000'
        }}> IAM Console</span> and
        click the blue <span style={{ fontWeight: 600 }}>Create Role</span> button. If you do not have
        permission to access this part of your AWS account, contact your AWS account admin.
      </Typography>

      <ol>
        <li>Select the following role type: <span style={{ fontWeight: 600 }}>Another AWS account.</span></li>
        <li>Enter <span style={{ fontWeight: 600 }}>{CONST_BUSINESS_ACCOUNT_ID}</span> for the <span style={{ fontWeight: 600 }}>Account ID.</span></li>
        <li>Check the box in order to <span style={{ fontWeight: 600 }}>Require external ID.</span></li>
        <li>Enter <span style={{ fontWeight: 600 }}>{CONST_BUSINESS_ACCOUNT_EXTERNAL_ID}</span> for the <span style={{ fontWeight: 600 }}>External ID.</span></li>
        <li>Click <span style={{ fontWeight: 600 }}>Next: Attach Policy.</span></li>
      </ol>

      <img src='/onboard-step1.png' alt='' width={'100%'} />
    </>
  )
}

export const Step2Details = () => {
  return (
    <>
      <Typography className='step-details-heading'>Step 2: Attach policy</Typography>

      <ol>
        <li>Enter <span style={{ fontWeight: 600 }}>ReadOnlyAccess</span> into the search box.</li>
        <li>Check the box for the <span style={{ fontWeight: 600 }}>ReadOnlyAccess</span> policy.</li>
        <li>Click <span style={{ fontWeight: 600 }}>Next: Role ARN.</span></li>
      </ol>

      <img src='/onboard-step2.png' alt='' width={'100%'} />
    </>
  )
}

export const Step3Details = () => {
  return (
    <>
      <Typography className='step-details-heading'>Step 3: Set role name and review</Typography>
      <Typography className='step-details-text'>
        Tip: {CONST_SITE_LABEL} HIGHLY RECOMMENDS using the role name
        <span style={{ fontWeight: 600 }}>{(CONST_SITE_LABEL ?? '').toLowerCase()}-tool-integration-role</span>. This name will show in
        the IAM console, and it should be something that reminds you that this role exists to grant {CONST_SITE_LABEL}
        Infrastructure access to monitor your AWS account.
      </Typography>

      <ol>
        <li>Enter <span style={{ fontWeight: 600 }}>{(CONST_SITE_LABEL ?? '').toLowerCase()}-tool-integration-role</span> for the <span style={{ fontWeight: 600 }}>Role name</span>.</li>
        <li>Click <span style={{ fontWeight: 600 }}>Create Role</span></li>
        <li>Copy the <span style={{ fontWeight: 600 }}>Role ARN:</span>
          <ol>
            <li>Select the role you have just created and enter the properties screen.</li>
            <li>Select the entire <span style={{ fontWeight: 600 }}>Role ARN</span> and copy it (you'll need it in the next step).</li>
          </ol>
        </li>
      </ol>

      <img src='/onboard-step3.png' alt='' width={'100%'} />
    </>
  )
}

export const Step4Details = () => {
  return (
    <>
      <Typography className='step-details-heading'>Step 1: Sign in to AWS Management Console and Open IAM Console</Typography>

      <ol>
        <li>Go to the IAM Console:
          <ul>
            <li>In the AWS Management Console, search for <span style={{ fontWeight: 600 }}>IAM</span> in the search bar and select the <span style={{ fontWeight: 600 }}>IAM</span> service</li>
          </ul>
        </li>
      </ol>

      <Typography className='step-details-heading margin-top'>Step 2: Locate and Select the IAM Role</Typography>

      <ol>
        <li>Navigate to Roles:
          <ul>
            <li>In the IAM console, click on <span style={{ fontWeight: 600 }}>Roles</span> in the left-hand menu</li>
          </ul>
        </li>
        <li>Search for Your Role:
          <ul>
            <li>Use the search bar to find the role created by your CloudFormation template (e.g., {(CONST_SITE_LABEL ?? '').toLowerCase()}-tool-integration-role)</li>
            <li>Change the sessionMaximum session duration to 12 hours</li>
            <li>Click on the role name to open its details</li>
          </ul>
          <img src='/onboard-inline-policy-step2.png' alt='' width={'100%'} />
        </li>
      </ol>

      <Typography className='step-details-heading margin-top'>Step 3: Add an Inline Policy to the Role</Typography>

      <ol>
        <li>Navigate to the Inline Policies Section:
          <ul>
            <li>On the role details page, scroll down to find the <span style={{ fontWeight: 600 }}>Permissions</span> tab</li>
            <li>Under <span style={{ fontWeight: 600 }}>Permissions policies</span>, click on <span style={{ fontWeight: 600 }}>Add inline policy</span></li>
          </ul>
          <img src='/onboard-inline-policy-step3.png' alt='' width={'100%'} />
        </li>
      </ol>

      <Typography className='step-details-heading margin-top'>Step 4: Create the Policy in the Json Editor</Typography>

      <ol>
        <li>Copy Policy from URL and paste in inline policy body:
          &nbsp;
          <a
            href='https://ca-policies.s3.ap-south-1.amazonaws.com/ca-iam-inline-policy.json'
            rel="noreferrer"
            target='_blank'
            className='action-link-core'
          >https://ca-policies.s3.ap-south-1.amazonaws.com/ca-iam-inline-policy.json</a>
          <img src='/onboard-inline-policy-step4.png' alt='' width={'100%'} style={{
            margin: '2vw 0',
          }} />
        </li>
      </ol>

      <Typography className='step-details-heading margin-top'>Step 5: Review Policy JSON and click on create</Typography>

      <img src='/onboard-inline-policy-step5.png' alt='' width={'100%'} />

      <Typography className='step-details-heading margin-top'>Step 6: Final output</Typography>

      <img src='/onboard-inline-policy-step6.png' alt='' width={'100%'} />
    </>
  )
}

export const Step5Details = () => {
  return (
    <>
      <Typography className='step-details-heading'>Step 4: Add Account Details</Typography>
      <Typography className='step-details-text'>Add your AWS account name as it will show in the {CONST_SITE_LABEL} UI.</Typography>
      <Typography className='step-details-text'>'We recommend you keep the same AWS account name. Changing it may break dashboards created by us.'</Typography>

      <ol>
        <li>Select the following role type: <span style={{ fontWeight: 600 }}>Another AWS account.</span></li>
        <li>Enter <span style={{ fontWeight: 600 }}>{CONST_BUSINESS_ACCOUNT_ID}</span> for the <span style={{ fontWeight: 600 }}>Account ID.</span></li>
        <li>Check the box in order to <span style={{ fontWeight: 600 }}>Require external ID.</span></li>
        <li>Enter <span style={{ fontWeight: 600 }}>{CONST_BUSINESS_ACCOUNT_EXTERNAL_ID}</span> for the <span style={{ fontWeight: 600 }}>External ID.</span></li>
        <li>Click <span style={{ fontWeight: 600 }}>Next: Attach Policy.</span></li>
      </ol>
    </>
  )
}

export const AutomationStep1Details = () => {
  return (
    <>
      <NoteDetails /><br />
      <Typography className='step-details-heading'>Step: Open the CloudFormation Launch URL</Typography>
      <Typography className='step-details-text'>
        Launch the Template: Open the <a
          href={CONST_ONBOARD_CLOUDFORMATION_LINK}
          target='_blank'
          rel="noreferrer"
          title='Click to open'
        >
          CloudFormation
        </a> url in your browser
      </Typography>

      <a
        href={CONST_ONBOARD_CLOUDFORMATION_LINK}
        target='_blank'
        rel="noreferrer"
        title='Click to open'
      >
        <img src='/launch-stack.webp' alt='' />
      </a>

      <Typography className='step-details-text'>This link will take you directly to the <span style={{ fontWeight: 600 }}>AWS CloudFormation</span> console with the template preloaded from the S3 URL</Typography>

      <Typography className='step-details-heading margin-top'>Step: Review Stack Details</Typography>

      <ol>
        <li>Stack Name:
          <ul>
            <li>In the <span style={{ fontWeight: 600 }}>Stack name</span> field, confirm or customize the name (e.g., {(CONST_SITE_LABEL ?? '').toLowerCase()}-tool-integration)</li>
          </ul>
        </li>
      </ol>

      <Typography className='step-details-heading margin-top'>Step: Acknowledge IAM Permissions</Typography>

      <ol>
        <li>Scroll Down to Capabilities Section:
          <ul>
            <li>At the bottom of the page, find the <span style={{ fontWeight: 600 }}>Capabilities</span> section</li>
          </ul>
        </li>
        <li>Acknowledge IAM Resource Creation:
          <ul>
            <li>Check the box to acknowledge that CloudFormation might create IAM resources on your behalf. This is required when creating IAM roles or policies</li>
          </ul>
        </li>
        <li>Review Details:
          <ul>
            <li>Double-check all details to make sure they are correct</li>
          </ul>
        </li>
      </ol>
    </>
  )
}

export const AutomationStep2Details = () => {
  return (
    <>
      <Typography className='step-details-heading'>Step: Create the Stack</Typography>

      <ol>
        <li>Click "Create Stack":
          <ul>
            <li>After reviewing the information, click on <span style={{ fontWeight: 600 }}>Create Stack</span> to initiate the deployment.</li>
          </ul>
        </li>
      </ol>

      <Typography className='step-details-heading margin-top'>Step: Monitor the Stack Creation</Typography>

      <ol>
        <li>View Status:
          <ul>
            <li>You’ll be redirected to the CloudFormation dashboard where the stack’s status will be <span style={{ fontWeight: 600 }}>CREATE_IN_PROGRESS</span>.</li>
            <li>Wait for the status to change to <span style={{ fontWeight: 600 }}>CREATE_COMPLETE</span>. This can take a few minutes.</li>
          </ul>
        </li>
      </ol>
    </>
  )
}

// export const AutomationStep5Details = () => {
//   return (
//     <>
//       <Typography variant='h6' style={{
//         color: '#505050',
//         marginBottom: '10px'
//       }}>
//         Step 5: Monitor the Stack Creation
//       </Typography>

//       <ol style={{
//         fontSize: '14px',
//         color: '#505050'
//       }}>
//         <li>View Status:
//           <ul>
//             <li>You’ll be redirected to the CloudFormation dashboard where the stack’s status will be <span style={{ fontWeight: 600 }}>CREATE_IN_PROGRESS</span>.</li>
//             <li>Wait for the status to change to <span style={{ fontWeight: 600 }}>CREATE_COMPLETE</span>. This can take a few minutes.</li>
//           </ul>
//         </li>
//       </ol>
//     </>
//   )
// }

export const AutomationStep3Details = () => {
  return (
    <>
      <Typography className='step-details-heading margin-top'>Step: Retrieve the Output ARN</Typography>

      <ol>
        <li>Go to the Outputs Tab:
          <ul>
            <li>Once the stack creation is complete, navigate to the <span style={{ fontWeight: 600 }}>Outputs</span> tab of the stack.</li>
          </ul>
        </li>
        <li>Copy the Output ARN and account and Paste on:
          &nbsp;
          <a
            href={`${CONST_BUSINESS_HOST}/dashboard`}
            target='_blank'
            rel="noreferrer"
          >{`${CONST_BUSINESS_HOST}/dashboard`}</a>
        </li>
      </ol>
    </>
  )
}

/**
 * AWS Onboarding Component
 * @returns 
 */
export const AWSOnboarding = () => {
  const [expanded, setExpanded] = useState(ONBOARDING_STEPS_CASE.AUTOMATION)
  const [isValidating, setIsValidating] = useState(false)
  const [APIErrorMessage, setAPIErrorMessage] = useState('')
  const [automationStep, setAutomationStep] = useState(0)
  const [manualStep, setManualStep] = useState(0)


  const { setIsOnboarded, refetchUserDetails } = useAuth()
  const navigate = useNavigate()
  const { setScopedItem, getScopedItem, removeScopedItem } = useScopedLocalStorage()

  const stepList = [
    {
      id: 1,
      label: 'Create Role'
    },
    {
      id: 2,
      label: 'Attach Policy'
    },
    {
      id: 3,
      label: 'Role ARN'
    },
    {
      id: 4,
      label: 'Inline Policy'
    },
    {
      id: 5,
      label: 'Account Details'
    },
    // {
    //   id: 5,
    //   label: 'Metric Stream'
    // },
  ]

  const automationStepList = [
    {
      id: 1,
      label: 'Step 1'
    },
    {
      id: 2,
      label: 'Step 2'
    },
    {
      id: 3,
      label: 'Step 3'
    }
  ]

  /**
   * Accordion change handler
   * @param {*} value 
   */
  const handleChange = (value) => {
    if (value.length > 0) {
      setExpanded(value);
      setAutomationStep(0);
      setManualStep(0);

      localStorage.removeItem('accountId');
      localStorage.removeItem('accountARN');
    }
  };

  const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
  ))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&::before': {
      display: 'none',
    },
  }));

  const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{
        fontSize: '0.9rem',
        // color: '#fff'
      }} />}
      {...props}
    />
  ))(({ theme }) => ({
    // backgroundColor: '#648c73',

    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }));

  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
  }));


  /**
   * Handle link account
   */
  const linkAccount = () => {
    setIsValidating(true)

    let tempAccountId = getScopedItem('accountId') ?? ''
    let tempAccountARN = getScopedItem('accountARN') ?? ''

    if (!tempAccountId || !tempAccountARN) {
      // Empty field validation
      setAPIErrorMessage('Please fill all required details!')
      setIsValidating(false)
    } else if (!validateAccountId(tempAccountId)) {
      // Invalid account id
      setAPIErrorMessage('Account ID should be 12 digit numeric value')
      setIsValidating(false)
    } else if (!validateAccountARN(tempAccountARN)) {
      // Invalid account arn validation
      setAPIErrorMessage('Please enter valid Account ARN')
      setIsValidating(false)
    } else {
      const email = JSON.parse(getScopedItem('userDetails'))['email'] ?? ''

      const requestBody = {
        "email": email,
        "accountId": tempAccountId,
        "accountArn": tempAccountARN,
        "userId": 0
      }

      if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (parseToJson['userId']) {
          requestBody['userId'] = parseToJson['userId']
        }
      }

      DebugService.log(`Onboard Request Component: Initiated account linking`, requestBody)

      RegistrationApiService.accountLink(requestBody)
        .then(response => {
          if (response['data'] && response['data']['message'] && response['data']['message'].includes('success')) {
            refetchUserDetails()
            setIsOnboarded(true)

            // Navigate to dashboard
            // navigateToDashboardPostLogin(CONST_ROUTE_PATH.DASHBOARD, true)
            navigateToDashboardPostLogin(navigate, true)
          }

          if (response['error'] && response['error']['message']) {
            setAPIErrorMessage(response['error']['message'])
            DebugService.error(`OnboardRequest Component: Caught error while account linking`, response, requestBody)
          }
        })
        .catch(e => {
          DebugService.error(`OnboardRequest Component: Caught error while account linking`, e, requestBody)
        })
        .finally(() => {
          removeScopedItem('accountId')
          removeScopedItem('accountARN')
          // checkAndNavigateToPrice(navigate)
          setIsValidating(false)
        })
    }
  }

  const nextStepHandle = (isAutomation) => {
    if (isAutomation) {
      const lastStep = automationStepList.length - 1
      if (automationStep < lastStep) {
        setAutomationStep(automationStep + 1)
      } else {
        linkAccount()
      }
    } else {
      const lastStep = stepList.length - 1
      if (manualStep < lastStep) {
        setManualStep(manualStep + 1)
      } else {
        linkAccount()
      }
    }
  }

  const backStepHandle = (isAutomation) => {
    if (isAutomation && automationStep > 0) {
      setAutomationStep(automationStep - 1)
    } else if (!isAutomation && manualStep > 0) {
      setManualStep(manualStep - 1)
    }
  }

  return (
    <Container maxWidth="lg" className="modern-onboarding-container">
      {/* Hero Section */}
      <Fade in timeout={800}>
        <Card className="onboarding-hero">
          <div className="onboarding-hero-content">
            <Typography variant="h2" className="onboarding-hero-title">
              Connect Your AWS Account
            </Typography>
            <Typography variant="h5" className="onboarding-hero-subtitle">
              Secure, fast, and intelligent cloud cost optimization starts here
            </Typography>
            <div className="platform-badge-large">
              <CloudUploadIcon className="platform-icon-large" />
              Amazon Web Services Integration
            </div>
          </div>
        </Card>
      </Fade>

      {/* Progress Indicator */}
      <Fade in timeout={1000}>
        <Card className="progress-container">
          <div className="progress-header">
            <Typography variant="h6" className="progress-title">
              Onboarding Progress
            </Typography>
            <Typography variant="h6" className="progress-percentage">
              {Math.round(((expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? automationStep : manualStep) / 
                (expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? automationStepList.length : stepList.length)) * 100)}%
            </Typography>
          </div>
          <LinearProgress 
            variant="determinate" 
            value={(expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? automationStep : manualStep) / 
              (expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? automationStepList.length : stepList.length) * 100}
            className="progress-bar"
          />
        </Card>
      </Fade>

      {/* Method Selection */}
      <Fade in timeout={1200}>
        <div className="method-selection">
          <Card 
            className={`method-card ${expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? 'selected' : ''}`}
            onClick={() => handleChange(ONBOARDING_STEPS_CASE.AUTOMATION)}
          >
            <div className="method-header">
              <div className="method-icon">
                <AutoAwesomeIcon />
              </div>
              <div>
                <Typography variant="h5" className="method-title">
                  Automated Setup
                </Typography>
                <Chip label="Recommended" size="small" color="primary" sx={{ mt: 1 }} />
              </div>
            </div>
            <Typography className="method-description">
              Use CloudFormation templates for quick, secure, and automated AWS account integration. 
              Perfect for teams who want to get started quickly with minimal manual configuration.
            </Typography>
            <ul className="method-features">
              <li>One-click CloudFormation deployment</li>
              <li>Automated IAM role and policy creation</li>
              <li>Secure cross-account access setup</li>
              <li>5-minute setup time</li>
            </ul>
          </Card>
          <Card 
            className={`method-card ${expanded === ONBOARDING_STEPS_CASE.MANUAL ? 'selected' : ''}`}
            onClick={() => handleChange(ONBOARDING_STEPS_CASE.MANUAL)}
          >
            <div className="method-header">
              <div className="method-icon">
                <ManualModeIcon />
              </div>
              <div>
                <Typography variant="h5" className="method-title">
                  Manual Setup
                </Typography>
                <Chip label="Advanced" size="small" color="secondary" sx={{ mt: 1 }} />
              </div>
            </div>
            <Typography className="method-description">
              Step-by-step manual configuration for organizations with specific security requirements 
              or custom IAM policies. Full control over every aspect of the integration.
            </Typography>
            <ul className="method-features">
              <li>Complete control over IAM configuration</li>
              <li>Custom policy implementation</li>
              <li>Detailed security review process</li>
              <li>Enterprise-grade compliance</li>
            </ul>
          </Card>
        </div>
      </Fade>

      {/* Automated Steps */}
      {expanded === ONBOARDING_STEPS_CASE.AUTOMATION && (
        <Zoom in timeout={600}>
          <div>
            {/* Modern Stepper */}
            <Card className="modern-stepper">
              <Stepper activeStep={automationStep} alternativeLabel>
                {automationStepList.map((step, index) => (
                  <Step key={step.id}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Card>

            {/* Step Content */}
            <Card className="step-content-card slide-in-up">
              <div className="step-header">
                <div className="step-icon">
                  {automationStep === 0 && <RocketIcon />}
                  {automationStep === 1 && <SettingsIcon />}
                  {automationStep === 2 && <LinkIcon />}
                </div>
                <Typography variant="h5" className="step-title">
                  {automationStepList[automationStep]?.label}
                </Typography>
              </div>
              <div className="step-content">
                {automationStep === 0 && <AutomationStep1Details />}
                {automationStep === 1 && <AutomationStep2Details />}
                {automationStep === 2 && <AutomationStep3Details />}
              </div>
            </Card>

            {/* Account Link Form */}
            {automationStep === 2 && (
              <Card className="modern-form-container slide-in-up">
                <Typography variant="h6" sx={{ mb: 3, color: '#1e293b', fontWeight: 600 }}>
                  Connect Your AWS Account
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Account ID"
                      placeholder="123456789012"
                      disabled={isValidating}
                      onChange={(e) => {
                        setScopedItem('accountId', e.target.value)
                        e.currentTarget.focus()
                      }}
                      inputProps={{ minLength: 12, maxLength: 12 }}
                      className="modern-form-field"
                      helperText="Your 12-digit AWS Account ID"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Role ARN"
                      placeholder={process.env.REACT_APP_AWS_ROLE_ARN_PLACEHOLDER}
                      disabled={isValidating}
                      onChange={(e) => {
                        setScopedItem('accountARN', e.target.value)
                        e.currentTarget.focus()
                      }}
                      inputProps={{ maxLength: 200 }}
                      className="modern-form-field"
                      helperText={`The ARN of the IAM role created for ${CONST_SITE_LABEL}`}
                    />
                  </Grid>
                </Grid>
                
                {APIErrorMessage.length > 0 && (
                  <div className="error-message">
                    {APIErrorMessage}
                  </div>
                )}
              </Card>
            )}

            {/* Action Buttons */}
            <Card className="modern-actions">
              <Button
                variant="outlined"
                disabled={automationStep === 0}
                onClick={() => backStepHandle(true)}
                className="modern-btn-back"
                startIcon={<ArrowForwardIosSharpIcon sx={{ transform: 'rotate(180deg)' }} />}
              >
                Back
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => nextStepHandle(true)}
                loading={isValidating}
                loadingPosition="end"
                className="modern-btn-next"
                endIcon={<ArrowForwardIosSharpIcon />}
              >
                {automationStep === automationStepList.length - 1 ? 'Complete Setup' : 'Continue'}
              </LoadingButton>
            </Card>
          </div>
        </Zoom>
      )}

      {/* Manual Steps */}
      {expanded === ONBOARDING_STEPS_CASE.MANUAL && (
        <Zoom in timeout={600}>
          <div>
            {/* Modern Stepper */}
            <Card className="modern-stepper">
              <Stepper activeStep={manualStep} alternativeLabel>
                {stepList.map((step, index) => (
                  <Step key={step.id}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Card>

            {/* Step Content */}
            <Card className="step-content-card slide-in-up">
              <div className="step-header">
                <div className="step-icon">
                  {manualStep === 0 && <AccountTreeIcon />}
                  {manualStep === 1 && <PolicyIcon />}
                  {manualStep === 2 && <VpnKeyIcon />}
                  {manualStep === 3 && <SettingsIcon />}
                  {manualStep === 4 && <LinkIcon />}
                </div>
                <Typography variant="h5" className="step-title">
                  {stepList[manualStep]?.label}
                </Typography>
              </div>
              <div className="step-content">
                {manualStep === 0 && <Step1Details />}
                {manualStep === 1 && <Step2Details />}
                {manualStep === 2 && <Step3Details />}
                {manualStep === 3 && <Step4Details />}
                {manualStep === 4 && <Step5Details />}
              </div>
            </Card>

            {/* Account Link Form */}
            {manualStep === 4 && (
              <Card className="modern-form-container slide-in-up">
                <Typography variant="h6" sx={{ mb: 3, color: '#1e293b', fontWeight: 600 }}>
                  Connect Your AWS Account
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Account ID"
                      placeholder="123456789012"
                      disabled={isValidating}
                      onChange={(e) => {
                        setScopedItem('accountId', e.target.value)
                        e.currentTarget.focus()
                      }}
                      inputProps={{ minLength: 12, maxLength: 12 }}
                      className="modern-form-field"
                      helperText="Your 12-digit AWS Account ID"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Role ARN"
                      placeholder={process.env.REACT_APP_AWS_ROLE_ARN_PLACEHOLDER}
                      disabled={isValidating}
                      onChange={(e) => {
                        setScopedItem('accountARN', e.target.value)
                        e.currentTarget.focus()
                      }}
                      inputProps={{ maxLength: 200 }}
                      className="modern-form-field"
                      helperText={`The ARN of the IAM role created for ${CONST_SITE_LABEL}`}
                    />
                  </Grid>
                </Grid>
                
                {APIErrorMessage.length > 0 && (
                  <div className="error-message">
                    {APIErrorMessage}
                  </div>
                )}
              </Card>
            )}

            {/* Action Buttons */}
            <Card className="modern-actions">
              <Button
                variant="outlined"
                disabled={manualStep === 0}
                onClick={() => backStepHandle(false)}
                className="modern-btn-back"
                startIcon={<ArrowForwardIosSharpIcon sx={{ transform: 'rotate(180deg)' }} />}
              >
                Back
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => nextStepHandle(false)}
                loading={isValidating}
                loadingPosition="end"
                className="modern-btn-next"
                endIcon={<ArrowForwardIosSharpIcon />}
              >
                {manualStep === stepList.length - 1 ? 'Complete Setup' : 'Continue'}
              </LoadingButton>
            </Card>
          </div>
        </Zoom>
      )}
    </Container>
  )
}